import React, { useEffect, useState } from 'react';
import CheckboxInput from '../../../../components/Forms/CheckboxInput';
import Input from "../../../../components/Forms/Input";
import SelectSimple from '../../../../components/Forms/SelectSimple';
import { useLanguage } from '../../../../hooks/Language';
import ArrayInputIncluderJoin from '../../../DashboardPages/core/components/Forms/ArrayInputIncluderJoin';
import TextareaInput from '../../../DashboardPages/core/components/Forms/TextareaInput';
import UploadCurriculumContent from '../../../DashboardPages/core/components/Forms/UploadCurriculumContent';

/**
 * Campos de upload que dependem de um campo "controlador".
 * O upload só aparece / é exigido quando o controlador estiver preenchido
 * com um valor DIFERENTE de "Não".
 *
 *   controlador           ->  campo de upload correspondente
 */
export const UPLOAD_CONTROLLED_FIELDS: Record<string, string> = {
    afirmativa: 'afirmativa_upload',
    requisicao: 'requisicao_upload',
    isencao: 'isencao_upload',
    atendimento_especial: 'atendimento_especial_upload',
};

/**
 * Título de seção exibido logo ACIMA do campo controlador, quando ele existir
 * na lista de fields.
 */
export const SECTION_TITLES: Record<string, string> = {
    afirmativa: 'Vagas Reservadas (Ações Afirmativas)',
    requisicao: 'Pontuação Adicional',
    isencao: 'Isenção da Taxa de Inscrição',
    atendimento_especial: 'Atendimento Especial',
};

/** Mapa reverso: campo de upload -> campo controlador. */
export const UPLOAD_TO_CONTROLLER: Record<string, string> = Object.keys(UPLOAD_CONTROLLED_FIELDS).reduce(
    (prev, controller) => {
        prev[UPLOAD_CONTROLLED_FIELDS[controller]] = controller;
        return prev;
    },
    {} as Record<string, string>,
);

/** Normaliza o valor: minúsculo, sem espaços e sem acento ("Não" -> "nao"). */
const normalize = (value: any): string =>
    `${value ?? ''}`
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

/**
 * true quando existe um valor preenchido e DIFERENTE de "Não".
 * Aceita string ou array (checkbox).
 */
export const hasRequestValue = (value: any): boolean => {
    if (value === undefined || value === null) return false;

    if (Array.isArray(value)) {
        const items = value.map(normalize).filter(v => v !== '');
        if (items.length === 0) return false;
        return items.some(v => v !== 'nao');
    }

    const normalized = normalize(value);
    if (normalized === '') return false;
    return normalized !== 'nao';
};

interface IFields {
    field: string;
    limit: number;
    label: string;
    title: string;
    description: string;
    type: string;
    options?: Array<string>;
    mask?: string;
}

interface IOptions {
    text?: string;
    label?: string;
    value: string | number;
}

interface IOptionsCheckbox {
    text?: string;
    label: string;
    value: string;
    id: string;
}


interface IProps {
    fields: Array<IFields>;
    setCallBack?: Function;
    /** Ref do <Form> do Unform (usado para ler os valores já preenchidos). */
    formRef?: any;
    /** Valores preservados (ex.: ao trocar a forma de pagamento) para semear a visibilidade. */
    initialValues?: Record<string, any>;
}

/** Extrai apenas os valores dos campos controladores de uma fonte de dados. */
const pickControllerValues = (source: Record<string, any> = {}): Record<string, any> => {
    const result: Record<string, any> = {};
    Object.keys(UPLOAD_CONTROLLED_FIELDS).forEach(controller => {
        result[controller] = source ? source[controller] : undefined;
    });
    return result;
};

const CompleteSaleFields: React.FC<IProps> = ({ fields, setCallBack = () => { }, formRef, initialValues }) => {
    const { translate } = useLanguage();

    // Guarda o valor atual de cada campo controlador para decidir a visibilidade dos uploads.
    const [controllerValues, setControllerValues] = useState<Record<string, any>>(
        () => pickControllerValues(initialValues),
    );

    // Compara dois valores de controlador (string ou array) para evitar re-render desnecessário.
    // Isso é essencial: alguns componentes reemitem o mesmo valor a cada render; sem esse "bail out"
    // o estado seria atualizado repetidamente, causando loop infinito.
    const isSameValue = (a: any, b: any): boolean => {
        if (a === b) return true;
        if (Array.isArray(a) && Array.isArray(b)) {
            if (a.length !== b.length) return false;
            return a.every((v, i) => v === b[i]);
        }
        return false;
    };

    // Lê os valores controladores diretamente do formulário (Unform) e atualiza o estado
    // apenas quando algo realmente mudar.
    const syncFromForm = () => {
        const data = formRef && formRef.current && formRef.current.getData ? formRef.current.getData() : undefined;
        if (!data) return;

        setControllerValues(prev => {
            const next = pickControllerValues(data);
            const changed = Object.keys(next).some(key => !isSameValue(next[key], prev[key]));
            return changed ? { ...prev, ...next } : prev;
        });
    };

    // Ao montar (inclusive quando o formulário remonta após trocar a forma de pagamento),
    // recupera os valores já preenchidos para calcular a visibilidade correta.
    useEffect(() => {
        syncFromForm();
        const timeout = setTimeout(syncFromForm, 0);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateController = (fieldName: string, value: any) => {
        setControllerValues(prev => (isSameValue(prev[fieldName], value) ? prev : { ...prev, [fieldName]: value }));
    };

    const readOptionsCheckbox = (texts) => {
        const options: Array<IOptionsCheckbox> = [];

        if (texts && texts.length > 0) {
            texts.map(option => {
                options.push({ label: option, value: option, id: option });
            })
        }

        return options;
    }
    const readOptions = (texts) => {
        const options: Array<IOptions> = [];

        if (texts && texts.length > 0) {
            texts.map(option => {
                options.push({ label: option, value: option });
            })
        }

        return options;
    }

    const readFields = (field: IFields, index: number) => {
        const fieldName = field?.field;

        // Se for um campo de upload controlado, só renderiza quando o controlador
        // tiver valor preenchido e diferente de "Não". Caso contrário, fica oculto
        // (e, por não estar montado, não é registrado nem exigido pelo Unform).
        if (fieldName && UPLOAD_TO_CONTROLLER[fieldName]) {
            const controller = UPLOAD_TO_CONTROLLER[fieldName];
            if (!hasRequestValue(controllerValues[controller])) {
                return <></>;
            }
        }

        // Se for um campo controlador, escutamos as mudanças para atualizar a visibilidade.
        const isController = !!(fieldName && UPLOAD_CONTROLLED_FIELDS[fieldName]);

        const valid = {
            checkbox: <><p style={{ margin: '10px auto', fontSize: '14px' }}>{translate(field?.description)}</p><CheckboxInput name={field?.field} options={readOptionsCheckbox(field?.options)} preText={<>{field?.title || ''}</>} setCallBack={isController ? (value) => updateController(fieldName, value) : undefined} /></>,
            select: <div style={{ width: '100%', margin: '10px auto' }}><p style={{ margin: '0 0 4px', fontSize: '14px', color: '#333' }}>{field?.title}</p>{field?.description ? <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#555' }}>{translate(field?.description)}</p> : null}<SelectSimple index={index + 1} name={field?.field} options={readOptions(field?.options)} label={''} setCallback={isController ? (value) => updateController(fieldName, value) : undefined} /></div>,
            input: <><p style={{ margin: '10px auto', fontSize: '14px' }}>{translate(field.description)}</p><Input name={field.field} placeholder={field.title} /></>,
            textarea: <TextareaInput name={field.field} placeholder={field.title} />,
            arrayInputIncluder: <><p>{field.title}</p><p style={{ fontSize: '14px' }}>{field.description}</p><ArrayInputIncluderJoin mask={field?.mask || ''} label={field.label} limit={field.limit} name={field.field} placeholder={field.title} /></>,
            image: <div style={{ margin: '10px auto',}}><p style={{ fontSize: '14px' }}>{field.title}</p><p style={{ fontSize: '14px' }}>{field.description}</p><UploadCurriculumContent label={field.label} name={field.field} /></div>
        };

        return valid[field?.type] ? valid[field?.type] : <></>;
    }

    // Container em largura total.
    // onClick (com setTimeout 0): relê o valor autoritativo do formulário DEPOIS que o toggle do
    // checkbox é aplicado — sem isso, o setCallBack do CheckboxInput chega com o valor anterior e a
    // visibilidade do _upload ficaria um clique atrás. É evento discreto + bail-out, então não gera loop.
    // onBlur: rede de segurança para controladores que não sejam <SelectSimple>/<CheckboxInput>.
    return <div style={{ width: '100%' }} onClick={() => setTimeout(syncFromForm, 0)} onBlur={syncFromForm}>
        {fields?.length > 0 ? <aside style={{ width: '100%', zIndex: 1000, position: 'relative', margin: '10px auto', background: '#ddd', height: '2px' }} /> : <></>}
        {
            fields.map((f, index) => {
                const sectionTitle = f?.field && SECTION_TITLES[f.field] ? SECTION_TITLES[f.field] : null;
                return (
                    <React.Fragment key={f?.field || index}>
                        {sectionTitle ? <h3 style={{ width: '100%', margin: '18px auto 6px', fontSize: '16px', color: '#333' }}>{translate(sectionTitle)}</h3> : null}
                        {readFields(f, index)}
                    </React.Fragment>
                );
            })
        }
        {fields.length > 0 ? <aside style={{ width: '100%', margin: '10px auto', background: '#ddd', height: '2px' }} /> : <></>}
    </div>;

}


export default CompleteSaleFields;
